'use client'

import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * FormLayout — composable form scaffolding.
 * Figma: Design System / Compositions / FormLayout (242:869).
 *
 * Use to stop hand-rolling Label/Input/Button spacing in every form.
 * Pairs with shadcn primitives (Input, Select, Button) and react-hook-form.
 */

export const FormLayout = React.forwardRef<
    HTMLFormElement,
    React.FormHTMLAttributes<HTMLFormElement>
>(({ className, ...props }, ref) => (
    <form ref={ref} className={cn('flex flex-col gap-6', className)} {...props} />
))
FormLayout.displayName = 'FormLayout'

/**
 * FormSection — labeled group with optional title and description.
 * Figma section gap: 24 between sections, 20 between fields inside.
 */
interface FormSectionProps extends React.HTMLAttributes<HTMLElement> {
    title?: string
    description?: string
}

export const FormSection = React.forwardRef<HTMLElement, FormSectionProps>(
    ({ className, title, description, children, ...props }, ref) => (
        <section ref={ref} className={cn('flex flex-col gap-4', className)} {...props}>
            {(title || description) && (
                <header className="flex flex-col gap-1">
                    {title && (
                        <h3 className="text-base font-semibold text-[var(--text-primary)] leading-6">
                            {title}
                        </h3>
                    )}
                    {description && (
                        <p className="text-sm text-[var(--text-secondary)] leading-5">
                            {description}
                        </p>
                    )}
                </header>
            )}
            <div className="flex flex-col gap-5">{children}</div>
        </section>
    ),
)
FormSection.displayName = 'FormSection'

/**
 * FormRow — horizontal multi-column row (collapses to single column on mobile).
 */
interface FormRowProps extends React.HTMLAttributes<HTMLDivElement> {
    columns?: 2 | 3
}

export const FormRow = React.forwardRef<HTMLDivElement, FormRowProps>(
    ({ className, columns = 2, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                'grid grid-cols-1 gap-4',
                columns === 2 && 'sm:grid-cols-2',
                columns === 3 && 'sm:grid-cols-3',
                className,
            )}
            {...props}
        />
    ),
)
FormRow.displayName = 'FormRow'

/**
 * FormField — single field wrapper: label + control + helper/error.
 * Pass the control (Input, Select, Textarea, etc.) as children.
 *
 * Figma field gap: 4 between label/control/helper.
 */
interface FormFieldProps extends React.HTMLAttributes<HTMLDivElement> {
    label?: React.ReactNode
    htmlFor?: string
    helperText?: React.ReactNode
    error?: React.ReactNode
    required?: boolean
}

export const FormField = React.forwardRef<HTMLDivElement, FormFieldProps>(
    ({ className, label, htmlFor, helperText, error, required, children, ...props }, ref) => {
        const showError = Boolean(error)
        return (
            <div ref={ref} className={cn('flex flex-col gap-1', className)} {...props}>
                {label && (
                    <label
                        htmlFor={htmlFor}
                        className="text-sm font-medium text-[var(--text-primary)] leading-5"
                    >
                        {label}
                        {required && (
                            <span className="ml-0.5 text-[var(--text-danger)]" aria-hidden="true">
                                *
                            </span>
                        )}
                    </label>
                )}
                {children}
                {showError ? (
                    <p
                        className="text-xs text-[var(--text-danger)] leading-4"
                        role="alert"
                    >
                        {error}
                    </p>
                ) : helperText ? (
                    <p className="text-xs text-[var(--text-tertiary)] leading-4">{helperText}</p>
                ) : null}
            </div>
        )
    },
)
FormField.displayName = 'FormField'

/**
 * FormFooter — right-aligned action row with top border.
 * Figma: padding-top 16, gap 8.
 */
export const FormFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={cn(
                'flex items-center justify-end gap-2 pt-4 border-t border-[var(--border-subtle)]',
                className,
            )}
            {...props}
        />
    ),
)
FormFooter.displayName = 'FormFooter'
